import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, Select, Button, Space, message, Modal, Alert, Radio } from 'antd';
import { SendOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  sendMessage, 
  batchSendMessage, 
  sendToAll, 
  sendByLevels,
  searchUsers 
} from '../../features/message/messageSlice';
import { fetchUserLevels } from '../../features/user/userSlice';

const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;

const MessageSend = () => {
  const dispatch = useDispatch();
  const { userLevels } = useSelector(state => state.user);
  const [form] = Form.useForm();
  const [recipientType, setRecipientType] = useState('users'); // 'all' | 'users' | 'levels'
  const [userOptions, setUserOptions] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [content, setContent] = useState('');
  const [selectMode, setSelectMode] = useState('single'); // 'single' | 'multiple'
  const [userPagination, setUserPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    dispatch(fetchUserLevels());
  }, [dispatch]);

  // 初始加载用户列表
  useEffect(() => {
    if (recipientType === 'users') {
      loadUsers(1, '');
    } else {
      // 切换类型时清空用户相关状态
      setUserOptions([]);
      setSearchKeyword('');
      setUserPagination({
        page: 1,
        limit: 20,
        total: 0,
        hasMore: false
      });
    }
  }, [recipientType]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // 加载用户列表
  const loadUsers = async (page = 1, keyword = '') => {
    setSearchingUsers(true);
    try {
      const limit = userPagination.limit || 20;
      const result = await dispatch(searchUsers({ 
        keyword, 
        page, 
        limit 
      })).unwrap();
      
      if (result && result.list) {
        if (page === 1) {
          setUserOptions(result.list);
        } else {
          setUserOptions(prev => [...prev, ...result.list]);
        }
        
        setUserPagination({
          page: result.pagination?.page || page,
          limit: result.pagination?.limit || limit,
          total: result.pagination?.total || 0,
          hasMore: (result.pagination?.page || page) < (result.pagination?.pages || 1)
        });
      }
    } catch (error) {
      message.error(error || '加载用户失败');
      if (page === 1) {
        setUserOptions([]);
      }
    } finally {
      setSearchingUsers(false);
      setLoadingMore(false);
    }
  };

  // 处理搜索框聚焦事件（点击时显示用户列表）
  const handleSelectFocus = () => {
    // 如果用户列表为空且不在加载中，则加载用户列表
    if (userOptions.length === 0 && !searchingUsers) {
      loadUsers(1, searchKeyword || '');
    }
  };

  // 用户搜索处理（防抖）
  const handleUserSearch = (keyword) => {
    setSearchKeyword(keyword);
    
    // 清除之前的定时器
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // 设置防抖
    searchTimeoutRef.current = setTimeout(() => {
      // 无论是否有关键词，都进行搜索（空关键词则加载所有用户）
      loadUsers(1, keyword ? keyword.trim() : '');
    }, 300);
  };

  // 加载更多用户
  const handleLoadMore = () => {
    if (loadingMore || !userPagination.hasMore) return;
    
    setLoadingMore(true);
    loadUsers(userPagination.page + 1, searchKeyword);
  };

  // 发送前确认（全部用户发送时需要确认）
  const handleConfirmAndSend = (values) => {
    if (recipientType === 'all') {
      confirm({
        title: '确认发送给所有用户？',
        icon: <ExclamationCircleOutlined />,
        content: '此操作将向所有正常状态的用户发送消息，请确认是否继续？',
        okText: '确认发送',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          handleSend(values);
        },
      });
    } else {
      handleSend(values);
    }
  };

  // 发送消息
  const handleSend = async (values) => {
    try {
      // 验证内容是否为空
      const textContent = content.replace(/<[^>]*>/g, '').trim();
      if (!textContent) {
        message.error('请输入消息内容');
        return;
      }

      const sendData = {
        messageType: values.messageType,
        title: values.title,
        content: content,
        templateCode: values.templateCode || null,
        extra: values.extra || null,
        channels: ['站内信']
      };

      if (recipientType === 'all') {
        // 发送给所有用户
        await dispatch(sendToAll(sendData)).unwrap();
        message.success('消息已成功发送给所有用户');
      } else if (recipientType === 'users') {
        // 发送给指定用户
        const userIds = selectMode === 'multiple' 
          ? (values.userIds || [])
          : (values.userId ? [values.userId] : []);
        
        if (userIds.length === 0) {
          message.error('请至少选择一个用户');
          return;
        }

        if (userIds.length === 1) {
          // 单个用户发送
          await dispatch(sendMessage({
            ...sendData,
            userId: userIds[0]
          })).unwrap();
          message.success('消息发送成功');
        } else {
          // 批量发送
          await dispatch(batchSendMessage({
            ...sendData,
            userIds: userIds
          })).unwrap();
          message.success(`成功向${userIds.length}个用户发送消息`);
        }
      } else if (recipientType === 'levels') {
        // 按等级发送
        const selectedLevels = values.userLevels || [];
        if (selectedLevels.length === 0) {
          message.error('请至少选择一个用户等级');
          return;
        }

        await dispatch(sendByLevels({
          ...sendData,
          userLevels: selectedLevels
        })).unwrap();
        message.success(`成功向等级 ${selectedLevels.join(', ')} 的用户发送消息`);
      }

      form.resetFields();
      setContent('');
      setRecipientType('users');
      setSearchKeyword('');
      setUserPagination({
        page: 1,
        limit: 20,
        total: 0,
        hasMore: false
      });
      // 重新加载用户列表
      if (recipientType === 'users') {
        loadUsers(1, '');
      }
    } catch (error) {
      message.error(error || '发送失败');
    }
  };

  return (
    <div>
      <Card title="发送消息" extra={<SendOutlined />}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleConfirmAndSend}
          initialValues={{
            recipientType: 'users',
            messageType: 'operation',
          }}
        >
          <Form.Item
            name="recipientType"
            label="发送对象类型"
            rules={[{ required: true, message: '请选择发送对象类型' }]}
          >
            <Select
              placeholder="请选择发送对象类型"
              onChange={(value) => {
                setRecipientType(value);
                form.setFieldsValue({
                  userIds: undefined,
                  userId: undefined,
                  userLevels: undefined
                });
              }}
            >
              <Option value="all">全部用户</Option>
              <Option value="users">指定用户</Option>
              <Option value="levels">按用户等级</Option>
            </Select>
          </Form.Item>

          {recipientType === 'all' && (
            <Alert
              message="将向所有正常状态的用户发送消息"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {recipientType === 'users' && (
            <>
              <Form.Item
                label="选择模式"
                style={{ marginBottom: 16 }}
              >
                <Radio.Group 
                  value={selectMode} 
                  onChange={(e) => {
                    setSelectMode(e.target.value);
                    form.setFieldsValue({
                      userIds: undefined,
                      userId: undefined
                    });
                  }}
                >
                  <Radio value="single">单选</Radio>
                  <Radio value="multiple">多选</Radio>
                </Radio.Group>
              </Form.Item>
              
              {selectMode === 'multiple' ? (
                <Form.Item
                  name="userIds"
                  label="选择用户"
                  rules={[{ required: true, message: '请至少选择一个用户' }]}
                  extra={`共 ${userPagination.total} 个用户，可以搜索手机号或姓名来查找用户`}
                >
                  <Select
                    mode="multiple"
                    placeholder="搜索并选择用户（输入手机号或姓名）"
                    showSearch
                    filterOption={false}
                    onFocus={handleSelectFocus}
                    onSearch={handleUserSearch}
                    loading={searchingUsers}
                    notFoundContent={
                      searchingUsers 
                        ? '搜索中...' 
                        : userOptions.length === 0 
                          ? '暂无用户数据' 
                          : '请输入关键词搜索用户'
                    }
                    optionLabelProp="label"
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {userPagination.hasMore && (
                          <div style={{ padding: '8px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
                            <Button
                              type="link"
                              loading={loadingMore}
                              onClick={handleLoadMore}
                              icon={<ReloadOutlined />}
                            >
                              加载更多 ({userPagination.total - userOptions.length} 个剩余)
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  >
                    {userOptions.map(user => (
                      <Option 
                        key={user.id} 
                        value={user.id}
                        label={`${user.realName || '未命名'} (${user.phone})`}
                      >
                        <div>
                          <div>{user.realName || '未命名'}</div>
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            {user.phone} | {user.levelName}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : (
                <Form.Item
                  name="userId"
                  label="选择用户"
                  rules={[{ required: true, message: '请选择一个用户' }]}
                  extra={`共 ${userPagination.total} 个用户，可以搜索手机号或姓名来查找用户`}
                >
                  <Select
                    placeholder="搜索并选择用户（输入手机号或姓名）"
                    showSearch
                    filterOption={false}
                    onFocus={handleSelectFocus}
                    onSearch={handleUserSearch}
                    loading={searchingUsers}
                    notFoundContent={
                      searchingUsers 
                        ? '搜索中...' 
                        : userOptions.length === 0 
                          ? '暂无用户数据' 
                          : '请输入关键词搜索用户'
                    }
                    optionLabelProp="label"
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {userPagination.hasMore && (
                          <div style={{ padding: '8px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
                            <Button
                              type="link"
                              loading={loadingMore}
                              onClick={handleLoadMore}
                              icon={<ReloadOutlined />}
                            >
                              加载更多 ({userPagination.total - userOptions.length} 个剩余)
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  >
                    {userOptions.map(user => (
                      <Option 
                        key={user.id} 
                        value={user.id}
                        label={`${user.realName || '未命名'} (${user.phone})`}
                      >
                        <div>
                          <div>{user.realName || '未命名'}</div>
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            {user.phone} | {user.levelName}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </>
          )}

          {recipientType === 'levels' && (
            <Form.Item
              name="userLevels"
              label="选择用户等级"
              rules={[{ required: true, message: '请至少选择一个用户等级' }]}
            >
              <Select
                mode="multiple"
                placeholder="请选择用户等级"
                optionLabelProp="label"
              >
                {userLevels
                  .filter(level => level.status === 1)
                  .map(level => (
                    <Option 
                      key={level.id} 
                      value={level.level}
                      label={`等级${level.level}: ${level.levelName}`}
                    >
                      <div>
                        <strong>等级{level.level}</strong>: {level.levelName}
                      </div>
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="messageType"
            label="消息类型"
            rules={[{ required: true, message: '请选择消息类型' }]}
          >
            <Select placeholder="请选择消息类型">
              <Option value="reward">奖励通知</Option>
              <Option value="logistics">物流通知</Option>
              <Option value="fund">资金通知</Option>
              <Option value="security">安全通知</Option>
              <Option value="operation">运营通知</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="消息标题"
            rules={[{ required: true, message: '请输入消息标题' }]}
          >
            <Input placeholder="请输入消息标题" />
          </Form.Item>

          <Form.Item
            label="消息内容"
            required
            validateStatus={content && content.replace(/<[^>]*>/g, '').trim() ? '' : 'error'}
            help={content && content.replace(/<[^>]*>/g, '').trim() ? '' : '请输入消息内容'}
            extra="支持富文本编辑，可以使用格式化工具栏进行样式设置"
          >
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              placeholder="请输入消息内容..."
              style={{ height: '300px', marginBottom: '50px' }}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'align': [] }],
                  ['link'],
                  ['clean']
                ],
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SendOutlined />}
                danger={recipientType === 'all'}
              >
                {recipientType === 'all' ? '确认发送给所有用户' : '发送消息'}
              </Button>
              <Button 
                onClick={() => {
                  form.resetFields();
                  setContent('');
                  setRecipientType('users');
                  setSearchKeyword('');
                  setUserPagination({
                    page: 1,
                    limit: 20,
                    total: 0,
                    hasMore: false
                  });
                  loadUsers(1, '');
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default MessageSend;
